/* eslint-disable spire/export-styles */
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import changeCustomerContext from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import NavigationModeMenu, {
    NavigationModeCookieName,
    NavigationModeMenuStyles,
} from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import { CustomState } from "@tggstore/Store/Reducers";
import { colorStrings, gutter } from "@tggstore/Utilities/variables";
import WarehouseSelector from "@tggstore/Widgets/VerndaleBox/WarehouseSelector";
import * as cssLinter from "css";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

const mapStateToProps = (state: CustomState) => {
    const { shipToId, isAuthenticated, rememberMe, isGuest } = getSession(state);
    const userIsAuthenticated = (isAuthenticated || rememberMe) && !isGuest;

    return {
        enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        currentMode: getCookie(NavigationModeCookieName) || "Storefront",
        // Verndale Customization
        customerNumber: getShipToState(state, shipToId).value?.customerNumber,
        userIsAuthenticated,
    };
};

const mapDispatchToProps = {
    changeCustomerContext,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

const Navigation = styled.div`
    font-size: 15px;
    text-transform: uppercase;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    inline-size: 100%;
    background-color: ${getColor(colorStrings.commonGlobalBlueLavender)};
    color: ${getColor(colorStrings.textMain)};
    height: 64px;
    padding: 0 ${gutter}px;
    position: relative;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 50%;
        right: 50%;
        height: 100%;
        background-color: ${getColor(colorStrings.commonGlobalBlueLavender)};
        z-index: -1;
        width: 100vw;
        transform: translateX(-50%);
    }

    &.wrapper--address-menu {
        justify-content: space-between;
        background-color: ${getColor(colorStrings.commonGlobalWhite)};
        position: relative;

        &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 50%;
            right: 50%;
            height: 100%;
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            z-index: -1;
            width: 100vw;
            transform: translateX(-50%);
        }
    }
`;

const NavItem = styled.span<InjectableCss>`
    display: inline-flex;

    > span,
    > a {
        margin-left: 10px;
    }
    ${injectCss}
`;

export interface SecondaryNavigationStyles extends VerndaleCustomizationStyles {
    wrapper?: InjectableCss;
    navigationModeMenu?: NavigationModeMenuStyles;
    showInventoryLink?: InjectableCss;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingLink?: InjectableCss;
    showPricingToggle?: ShowPricingToggleStyles;
    currencyLink?: InjectableCss;
    languageLink?: InjectableCss;
    warehousePickupLink?: InjectableCss;
    signInLink?: InjectableCss;
}

interface VerndaleCustomizationStyles {
    warehouseSelectMenuWrapper?: InjectableCss;
}

// Verndale Customization
// src/FrontEnd/modules/blueprints/tggstore/src/WidgetExtensions/Header/SecondaryNavigationExtension.ts
export const secondaryNavigationStyles: SecondaryNavigationStyles = {
    showInventoryLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    showPricingLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    currencyLink: {
        css: css`
            margin-left: 16px;
        `,
    },
    languageLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
        `,
    },
    warehousePickupLink: {
        css: css`
            flex: 0 1 auto;
            margin-left: 6px;
            min-width: 30px;
        `,
    },
    signInLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 6px;
        `,
    },
};

const defaultCustomCss = `.wrapper {
}

.navigation-mode-menu {
}

.currency-menu {
}

.language-menu {
}

.ship-to-address-menu {
}

.sign-in-link {
}`;

const SecondaryNavigation: FC<Props> = ({
    id,
    enableWarehousePickup,
    displayPricingAndInventory,
    currentMode,
    fields,
    // Verndale Customization
    customerNumber,
    userIsAuthenticated,
}) => {
    const styles = useMergeStyles("secondaryNavigation", secondaryNavigationStyles);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <Hidden below="lg">
                {/* Verndale Customization */}
                <GridContainer
                    gap={0}
                    isFullWidth={true}
                    css={css`
                        width: 100%;
                    `}
                >
                    {enableWarehousePickup && userIsAuthenticated && (
                        <GridItem width={12}>
                            <Navigation className="wrapper--address-menu" {...styles.wrapper}>
                                <NavItem className="ship-to-address-menu" {...styles.warehousePickupLink}>
                                    <Zone zoneName="ShipToAddress" contentId={id} fixed />
                                </NavItem>
                                {customerNumber && (
                                    <NavItem className="ship-to-address-menu" {...styles.warehousePickupLink}>
                                        <span>{translate("ISC-Customer-#")}:&nbsp;</span>
                                        {customerNumber}
                                    </NavItem>
                                )}
                            </Navigation>
                        </GridItem>
                    )}
                    <GridItem width={12}>
                        <Navigation className="wrapper" {...styles.wrapper}>
                            {/* Verndale Customization */}
                            {userIsAuthenticated && (
                                <NavItem className="warehouse-menu" {...styles.warehouseSelectMenuWrapper}>
                                    {/* <Zone zoneName="ISC-WarehouseSelector" contentId={id} /> */}
                                    <WarehouseSelector isMobile={false} />
                                </NavItem>
                            )}

                            {/* END Verndale Customization */}
                            <NavItem className="navigation-mode-menu">
                                <NavigationModeMenu extendedStyles={styles.navigationModeMenu} />
                            </NavItem>
                            {fields.showInventoryToggle && displayPricingAndInventory && currentMode === "Storefront" && (
                                <NavItem
                                    className="show-inventory-toggle"
                                    data-test-selector="showInventoryToggle"
                                    {...styles.showInventoryLink}
                                >
                                    <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                                </NavItem>
                            )}
                            {fields.showPricingToggle && displayPricingAndInventory && currentMode === "Storefront" && (
                                <NavItem
                                    className="show-pricing-toggle"
                                    data-test-selector="showPricingToggle"
                                    {...styles.showPricingLink}
                                >
                                    <ShowPricingToggle extendedStyles={styles.showPricingToggle} />
                                </NavItem>
                            )}

                            <NavItem className="currency-menu" {...styles.currencyLink}>
                                <Zone zoneName="Currency" contentId={id} fixed />
                            </NavItem>
                            <NavItem className="language-menu" {...styles.languageLink}>
                                <Zone zoneName="Language" contentId={id} fixed />
                            </NavItem>
                            <NavItem className="sign-in-link" {...styles.signInLink}>
                                <Zone zoneName="SignIn" contentId={id} fixed />
                            </NavItem>
                        </Navigation>
                    </GridItem>
                </GridContainer>
                {/* END Verndale Customization */}
            </Hidden>
        </StyledWrapper>
    );
};

const secondaryNavigation: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(SecondaryNavigation)),

    definition: {
        group: "Common",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default secondaryNavigation;
